import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Page, Pagebar, PagebarActions, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useList } from '../../../../modules/hooks/tools/use-list';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { CreativeModel } from '../../../../modules/models/creative.model';
import { useCreativeActions } from '../../../../modules/hooks/actions/creatives.actions';
import { IPerformanceOptionsFilterType } from '../../../../modules/hooks/apis/use-performance';
import { useMergeWithPerformance } from '../../../../modules/hooks/tools/use-merge-with-performance';
import { usePerformanceEntity } from '../../../../modules/hooks/apis/use-performance-entity';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { useCreativeHeaders } from '../../../../modules/hooks/theaders/creatives.headers';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { IAdminxStore } from '../../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { getFilterObject, PerformancesColumnsSnakeCase } from '../../../../modules/hooks/apis/use-reports-list';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { useMemo } from 'react';

export const AdvertiserCreatives = observer(() => {
	const { creativeStore } = useStores<IAdminxStore>();
	const { advertiserId } = useParams();
	const { currentRouteWithoutSpecialChars } = useRoute();

	const performanceStore = creativeStore.getPerformance();

	const options = useMemo(() => (advertiserId ? { queryParams: { advertiserId } } : undefined), [advertiserId]);
	useList<CreativeModel>(creativeStore.getListStore(), options);

	const currentFilters = usePageSettings()?.tableStore?.getFiltersStore(currentRouteWithoutSpecialChars)?.getCurrentFilters();
	const performanceFilters = getFilterObject(currentFilters || new Map(), 'adxCreatives');

	if (advertiserId) {
		performanceFilters.push({
			column: PerformancesColumnsSnakeCase.AdvertiserId,
			filterType: IPerformanceOptionsFilterType.Include,
			value: [advertiserId],
		});
	}

	usePerformanceEntity(performanceStore, {
		filters: performanceFilters,
		reportName: 'adxCreatives',
	});

	const { data, summary } = useMergeWithPerformance<CreativeModel>({
		performanceStore,
		data: creativeStore.getListStore().getList(),
		key: PerformancesColumnsSnakeCase.CreativeId,
		attachExtraParams(row) {
			return {
				brand: row.brand,
				adx_creative_id: row.adx_creative_id,
				name: row.brand,
				description: row.description,
				resource_url: row.resource_url,
				resource_type: row.resource_type,
				adx_creative_status: row.adx_creative_status,
				adx_network_status: row.adx_network_status,
				adx_deals_status: row.adx_deals_status,
				size: row.size,
				default_dest_url: row.default_dest_url,
				creative_group: row.creative_group,
			};
		},
	});

	const { SelectedActions, FiltersActions, SegmentActions, EntityActions } = useCreativeActions();
	const { columns } = useCreativeHeaders({ EntityActions, summary });

	return (
		<Page>
			<Pagebar>
				<PagebarTitle>Creatives</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
				</PagebarActions>
			</Pagebar>
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'creatives_advertiser_table')] }}
				data={data}
				isLoading={creativeStore.getListStore().getIsLoading()}
				isSummary={true}>
				{() => {
					return {
						SelectedActions,
						FiltersActions,
						SegmentActions,
					};
				}}
			</ControlledTable>
		</Page>
	);
});
